import { Component, Prop, h, Element } from '@stencil/core';

const COMPONENT_NAME = 'me-section';

const DEFAULTS = {
  yGap: '5xl',
  yGapLg: '7xl',
} as const;

/**
 * A flexible section wrapper component.
 *
 * Renders a semantic HTML tag (e.g. `section`, `main`, `header`) and optionally wraps its contents
 * in a `<me-container>`, with configurable vertical spacing.
 *
 * Passes through additional attributes such as `id`, `aria-*`, and `data-*` to the rendered tag.
 */
@Component({
  tag: 'me-section',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Section {
  /** Reference to the host element (<me-section>) */
  @Element() host: HTMLElement;

  /**
   * The semantic HTML tag to render as the root of the component.
   * Defaults to `'section'`.
   */
  @Prop() tag: 'div' | 'main' | 'footer' | 'header' | 'article' | 'section' = 'section';

  /**
   * If true, disables the inner `<me-container>` wrapper.
   * Use this when you want full control over the layout without extra padding.
   */
  @Prop() noContainer: boolean = false;

  /**
   * Defines the vertical spacing for the inner `<me-container>`.
   * Only applies when `noContainer` is false.
   */
  @Prop() yGap: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.yGap;
  @Prop() yGapLg: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.yGapLg;

  /**
   * Merges the default component class with any custom class
   * provided on the host element (`<me-section class="...">`).
   */
  private getMergedClasses(): string {
    const hostClass = this.host.getAttribute('class') ?? '';
    return `${COMPONENT_NAME} ${hostClass}`.trim();
  }

  /**
   * Extracts all attributes from the host element that are not known props,
   * so they can be passed through to the rendered tag.
   *
   * This allows support for native attributes like `id`, `data-*`, `aria-*`, etc.
   */
  private getExtraAttributes(): { [key: string]: string } {
    const knownProps = ['tag', 'no-container', 'vertical-spacing', 'y-gap', 'y-gap-lg', 'class'];
    const attrs: { [key: string]: string } = {};

    Array.from(this.host.attributes).forEach(attr => {
      if (!knownProps.includes(attr.name)) {
        attrs[attr.name] = attr.value;
      }
    });

    return attrs;
  }

  render() {
    const Tag = this.tag;
    const className = this.getMergedClasses();
    const extraAttrs = this.getExtraAttributes();

    return (
      <Tag class={className} {...extraAttrs}>
        {this.noContainer ? (
          <slot />
        ) : (
          <me-container yGap={this.yGap} yGapLg={this.yGapLg}>
            <slot />
          </me-container>
        )}
      </Tag>
    );
  }
}
